import React, { useEffect, useState, useRef } from "react";
import DataTable from "../../Pages/DataTable";
import { batchStatusAPI } from "../../Pages/api";
import { getFuncAreas, getAllTrngCourses } from "../../Pages/cache";

export default function BatchViewer({
  autoLoad = true,
  selectable = false,
  onSelect = () => {},
  filterStatus = null,
  loadTrigger = null,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const closeTimer = useRef(null);
  const [funcAreaMap, setFuncAreaMap] = useState({});
  const [courseMap, setCourseMap] = useState({});

  const [hasMore, setHasMore] = useState(false);
  const [lastKey, setLastKey] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const statusList = Array.isArray(filterStatus)
    ? filterStatus
    : filterStatus ? [filterStatus] : [];

  const applyStatusFilter = (items) =>
    statusList.length ? items.filter(i => statusList.includes((i.trng_status || '').toUpperCase())) : items;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [funcAreas, courses] = await Promise.all([getFuncAreas(), getAllTrngCourses()]);
      const fMap = {};
      (Array.isArray(funcAreas) ? funcAreas : []).forEach(f => { fMap[String(f.func_sub_area_code)] = f.func_sub_area_name; });
      setFuncAreaMap(fMap);
      const cMap = {};
      (Array.isArray(courses) ? courses : []).forEach(c => { cMap[String(c.trng_course_code)] = c.trng_course_name; });
      setCourseMap(cMap);

      // Paginate through all batches automatically
      let allItems = [];
      let cursor = null;
      do {
        const res = await batchStatusAPI.getPage({ limit: 200 }, cursor);
        const page = Array.isArray(res.items) ? res.items : [];
        allItems = allItems.concat(page);
        cursor = res.hasMore ? (res.lastKey ?? null) : null;
      } while (cursor);

      const filtered = applyStatusFilter(allItems);
      setRows([...filtered].sort((a, b) => Number(b.batch_sr_no ?? 0) - Number(a.batch_sr_no ?? 0)));
      setLastKey(null);
      setHasMore(false);
      setCurrentPage(1);
      setSelectedIdx(null);
    } catch (e) {
      setRows([]);
      setError("Failed to load batches. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!lastKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = { limit: 50 };
      const res = await batchStatusAPI.getPage(params, lastKey);
      const items = applyStatusFilter(Array.isArray(res.items) ? res.items : []);
      setRows((prev) => [...prev, ...items].sort((a, b) => Number(b.batch_sr_no ?? 0) - Number(a.batch_sr_no ?? 0)));
      setLastKey(res.lastKey ?? null);
      setHasMore(res.hasMore ?? !!res.lastKey);
    } catch (e) {
      setError('Failed to load more batches.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (autoLoad) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, loadTrigger]);

  useEffect(() => {
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, []);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const pagedRows = rows.slice(startIdx, startIdx + pageSize);

  const handleRadioSelect = (pageRelativeIdx) => {
    const absoluteIdx = startIdx + pageRelativeIdx;
    setSelectedIdx(absoluteIdx);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      onSelect(rows[absoluteIdx]);
    }, 500);
  };

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : "";

  const columns = [
    ...(selectable
      ? [{
          header: "Accept",
          className: "dt-col-narrow",
          renderCell: (_val, _row, pageRelativeIdx) => (
            <input
              type="radio"
              name="batchSelect"
              checked={selectedIdx === startIdx + pageRelativeIdx}
              onChange={() => handleRadioSelect(pageRelativeIdx)}
            />
          ),
        }]
      : []),
    { header: "Batch Sr. No.", accessor: (row) => row.batch_sr_no ?? "", className: "dt-col-sm" },
    { header: "Functional Area", accessor: (row) => funcAreaMap[String(row.func_sub_area_code)] ?? row.func_sub_area_code ?? "", className: "dt-col-md" },
    { header: "Training Course Name", accessor: (row) => courseMap[String(row.trng_course_code)] ?? row.trng_course_code ?? "", className: "dt-col-lg" },
    { header: "Start Date", accessor: (row) => fmtDate(row.start_date), className: "dt-col-sm" },
    { header: "End Date", accessor: (row) => fmtDate(row.end_date), className: "dt-col-sm" },
    { header: "Batch Size", accessor: (row) => row.batch_size ?? "", className: "dt-col-sm" },
    { header: "No. Of Days", accessor: (row) => {
      if (!row.start_date || !row.end_date) return "";
      return Math.floor((new Date(row.end_date) - new Date(row.start_date)) / (1000 * 60 * 60 * 24)) + 1;
    }, className: "dt-col-sm" },
  ];

  const pgBtnStyle = {
    padding: "3px 10px",
    border: "1px solid #ccc",
    borderRadius: 4,
    background: "#f7f7f7",
    cursor: "pointer",
    fontFamily: "GEG-Body, sans-serif",
    fontSize: 12,
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button
          type="button"
          onClick={load}
          style={{ ...pgBtnStyle, background: "#810055", color: "#fff", border: "none", borderRadius: 30, padding: "4px 14px" }}
        >
          Refresh
        </button>
      </div>

      {loading && (
        <p style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5, color: "#666", marginTop: 10 }}>
          Loading batches…
        </p>
      )}

      {error && (
        <p style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5, color: "red", marginTop: 10 }}>
          {error}
        </p>
      )}

      {!loading && (
        <>
          <DataTable
            columns={columns}
            data={pagedRows}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => {}}
            onChangeEditCell={() => {}}
            selectedForDelete={{}}
            onToggleDeleteRow={() => {}}
            emptyMessage="No batches found"
            rowKey={(row, i) => `${row.batch_sr_no}-${startIdx + i}`}
          />

          {rows.length > 0 && (
            <>
              {selectable && (
                <div style={{ display: "flex", justifyContent: "center", margin: "10px 0 4px" }}>
                  <button
                    type="button"
                    onClick={() => { if (selectedIdx !== null) onSelect(rows[selectedIdx]); }}
                    disabled={selectedIdx === null}
                    style={{ background: "#810055", color: "#fff", border: "none", borderRadius: 30, padding: "5px 28px", fontFamily: "GEG-Body, sans-serif", fontSize: 12.5, cursor: selectedIdx === null ? "not-allowed" : "pointer" }}
                  >
                    Submit
                  </button>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, margin: "6px 0 0", fontFamily: "GEG-Body, sans-serif", fontSize: 12 }}>
                <button type="button" style={pgBtnStyle} disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>&laquo;</button>
                <button type="button" style={pgBtnStyle} disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>&lsaquo;</button>
                <span>Page {currentPage} of {totalPages}</span>
                <button type="button" style={pgBtnStyle} disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>&rsaquo;</button>
                <button type="button" style={pgBtnStyle} disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>&raquo;</button>
                {hasMore && (
                  <button type="button" onClick={loadMore} disabled={loadingMore}
                    style={{ ...pgBtnStyle, background: "#810055", color: "#fff", border: "none", borderRadius: 30, padding: "3px 12px" }}>
                    {loadingMore ? "Loading…" : "Load More"}
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
